import { useState } from 'react';
import { useModalDialog } from '@/pages/fulfillment/components/useModalDialog';
import { CANCEL_INBOUND_MEMO_LIMIT, type CancelInboundLineRequest } from '@/infrastructure/FulfillmentAdminClient';
import {
  CANCEL_INBOUND_MEMO_PLACEHOLDER,
  cancelInboundShelfRole,
} from '@/pages/fulfillment/components/cancelInboundShelf';

export interface CancelInboundTarget {
  orderId: number;
  lineNo: number;
  sku: string;
  /** How many units the arrival booked; the restock defaults to all of them and may not exceed them. */
  arrivedQty: number;
  /** The shelf the arrival is booked on (the order's Shelf when the booking is unknown). */
  suggestedLocation: string;
  /**
   * The booking is known, so the field is not editable: the server takes the write-off off the shelf named
   * here and a restock moves nothing, so another shelf would only take stock off the wrong one.
   */
  locationLocked?: boolean;
}

export interface CancelInboundModalProps {
  target: CancelInboundTarget;
  busy: boolean;
  onClose: () => void;
  onConfirm: (request: CancelInboundLineRequest) => void;
}

/**
 * Taking an arrival back. No restock is the default (SSD-7674, Yongwon 10/24): a cancel usually undoes an
 * arrival booked by mistake, so the units come off the balance unless the operator says they stay. Choosing
 * Yes puts them back on a shelf as free stock, and a restock smaller than the arrival writes the difference
 * off — which is why both paths still name a shelf [BR-58].
 */
export function CancelInboundModal({ target, busy, onClose, onConfirm }: CancelInboundModalProps) {
  const [restock, setRestock] = useState(false);
  const [restockQty, setRestockQty] = useState(String(target.arrivedQty));
  const [locationCode, setLocationCode] = useState(target.suggestedLocation);
  const [memo, setMemo] = useState('');

  const trimmed = locationCode.trim();
  const trimmedMemo = memo.trim();
  const qty = Number(restockQty);
  // Zero is what declining already says, and more than arrived never physically happened.
  const qtyValid = Number.isInteger(qty) && qty >= 1 && qty <= target.arrivedQty;
  const blocked = busy || !trimmed || (restock && !qtyValid);
  const writtenOff = restock && qtyValid ? target.arrivedQty - qty : 0;

  const dialogRef = useModalDialog(onClose);

  return (
    <dialog className="vo-modal" ref={dialogRef} aria-label="Cancel Inbound">
      <header>
        Cancel Inbound — {target.sku}
        <button type="button" className="x" aria-label="Close" onClick={onClose}>
          ✕
        </button>
      </header>
      <div className="body cim">
        <fieldset className="cim-field">
          <legend className="cim-label">
            <span className="cim-n">1</span>Restock this inventory?
          </legend>
          <div className="cim-seg" role="radiogroup" aria-label="Restock this inventory?">
            <label className={restock ? 'on' : ''}>
              <input type="radio" name="cancel-inbound-restock" checked={restock} onChange={() => setRestock(true)} />
              Yes — restock
            </label>
            <label className={!restock ? 'on' : ''}>
              <input type="radio" name="cancel-inbound-restock" checked={!restock} onChange={() => setRestock(false)} />
              No
            </label>
          </div>
          <p className="cim-help">
            {restock
              ? 'The units go back on a shelf as free stock.'
              : 'The units the arrival added are taken off stock.'}
          </p>
        </fieldset>

        <div className={`cim-field${restock ? '' : ' off'}`}>
          <label className="cim-label" htmlFor="cim-qty">
            <span className="cim-n">2</span>Restock Qty
          </label>
          <div className="cim-qty">
            <input
              id="cim-qty"
              type="number"
              aria-label="Restock Qty"
              min={1}
              max={target.arrivedQty}
              value={restockQty}
              disabled={!restock}
              onChange={(event) => setRestockQty(event.target.value)}
            />
            <span>of {target.arrivedQty} arrived</span>
          </div>
          <p className="cim-help">
            {writtenOff > 0
              ? `${writtenOff} unit(s) will be written off.`
              : 'A smaller figure writes the difference off.'}
          </p>
        </div>

        <div className="cim-field">
          <label className="cim-label" htmlFor="cim-loc">
            <span className="cim-n">3</span>Location
          </label>
          <input
            id="cim-loc"
            className="cim-input"
            type="text"
            aria-label="Location"
            value={locationCode}
            readOnly={target.locationLocked}
            onChange={(event) => setLocationCode(event.target.value)}
          />
          <p className="cim-help">
            {cancelInboundShelfRole(restock, writtenOff)}
            {target.locationLocked
              ? ' · where this arrival is booked, so it is not edited here'
              : writtenOff === 0 && ' · prefilled with the shelf the arrival was booked on'}
          </p>
          {!trimmed && <p className="cim-need">Type the shelf first — Cancel Inbound stays off until it has one.</p>}
        </div>

        <div className="cim-field">
          <label className="cim-label" htmlFor="cim-memo">
            <span className="cim-n">4</span>Memo <span className="cim-opt">(Optional)</span>
          </label>
          <textarea
            id="cim-memo"
            className="cim-input"
            aria-label="Memo (Optional)"
            rows={3}
            maxLength={CANCEL_INBOUND_MEMO_LIMIT}
            placeholder={CANCEL_INBOUND_MEMO_PLACEHOLDER}
            value={memo}
            onChange={(event) => setMemo(event.target.value)}
          />
        </div>

        <div className="note">
          {restock
            ? 'The line owes its goods again; the units stay on the shelf, unreserved, for whoever needs them next.'
            : 'The line owes its goods again, and the units the arrival added are taken off the shelf.'}
        </div>
      </div>
      <div className="foot">
        <button type="button" className="btn btn-line btn-sm" onClick={onClose}>
          Keep the inbound
        </button>
        <button
          type="button"
          className="btn btn-sm cim-danger"
          disabled={blocked}
          onClick={() =>
            onConfirm({
              ...(restock
                ? { restock: true, ...(qty < target.arrivedQty && { restockQty: qty }), locationCode: trimmed }
                : { restock: false, locationCode: trimmed }),
              ...(trimmedMemo && { memo: trimmedMemo }),
            })
          }
        >
          Cancel Inbound
        </button>
      </div>
    </dialog>
  );
}
